import React from "react";
import { shallowEqual, useSelector } from "react-redux";
import { addCurrencyWord } from "../../../../utils";
import { getServiceName } from "../../../../resources/subscriptionsMock";
import { Button, NoticeBanner } from "../../../../components";
import { selectNoticesFor } from "../../../../redux/notices/selectors";

import styles from "./SubscrItem.module.scss";

import { SubscrItemPropsType, ITempBasketItem } from "./types";
import { ISubscription } from "../../../../redux/subscriptions/types";
import gym from "../../../../assets/icons/gym.png";
import pool from "../../../../assets/icons/whaterpool.png";
import sauna from "../../../../assets/icons/sauna.png";
import playroom from "../../../../assets/icons/igroteka.png";

export const SubscrItem: React.FC<SubscrItemPropsType> = (props) => {
  const {
    onQuantityChange,
    onFamilyCountChange,
    selectedSubscriptions = [],
    disabled = false,
  } = props;

  const {
    id,
    title,
    type,
    expiration_text,
    includes,
    price,
    subtitle,
    durationNote,
    visitingHours,
    ageRestriction,
    basePrice,
    additionalPersonPrice,
    minPeopleRequired,
    isNew,
    isBest,
    isGroupClassesIncluded,
    requiresAdult,
    note,
  } = props.data as ISubscription & {
    subtitle?: string;
    durationNote?: string;
    visitingHours?: string;
    ageRestriction?: string;
    basePrice?: number;
    additionalPersonPrice?: number;
    minPeopleRequired?: number;
    isNew?: boolean;
    isBest?: boolean;
    isGroupClassesIncluded?: boolean;
    requiresAdult?: boolean;
    note?: string;
    type?: "adult" | "kid" | "family" | "gift" | "daily" | "child";
  };

  const subType = ((props.data as any).mockType || type || (props.data as any).type) as 'family' | 'adult' | 'child' | 'daily' | 'gift';

  const existingItem = selectedSubscriptions.find((sub) => sub.id === id);

  // id абонемента — это membershipId из 1С
  const cardNotices = useSelector(selectNoticesFor("SUBSCRIPTION_CARD", id), shallowEqual);

  const [quantity, setQuantity] = React.useState(
    existingItem && existingItem.type !== "family" ? existingItem.quantity || 0 : 0
  );
  const [quantityAdult, setQuantityAdult] = React.useState(
    existingItem && existingItem.type === "family" ? existingItem.adultsCount || 0 : 0
  );
  const [quantityKid, setQuantityKid] = React.useState(
    existingItem && existingItem.type === "family" ? existingItem.childrenCount || 0 : 0
  );

  const hasAdultSubscription = selectedSubscriptions.some((sub) => sub.type === "adult");
  const hasFamilyInBasket = selectedSubscriptions.some((sub) => sub.type === "family");
  const hasGiftInBasket = selectedSubscriptions.some((sub) => {
    const subTypeValue = (sub as any).type || sub.type;
    return subTypeValue === "gift";
  });
  const hasDailyInBasket = selectedSubscriptions.some((sub) => sub.type === "daily");

  const isChildDisabled = subType === "child" && !hasAdultSubscription && selectedSubscriptions.length > 0;
  const isBlockedByFamily = hasFamilyInBasket && subType !== "family";
  const isBlockedByGift = hasGiftInBasket && subType !== "gift";
  const isBlockedByDaily = hasDailyInBasket && subType !== "daily";
  const hasNonGiftNonChildItems = selectedSubscriptions.some(
    (sub) => sub.type !== "gift" && sub.type !== "child" && String(sub.id) !== String(id)
  );
  const isBlockedForGift = subType === "gift" && hasNonGiftNonChildItems;

  const isCompletelyBlocked =
    disabled || isChildDisabled || isBlockedByFamily || isBlockedByGift || isBlockedByDaily || isBlockedForGift;

  React.useEffect(() => {
    if (existingItem) {
      if (subType === "family") {
        setQuantityAdult(existingItem.adultsCount || 0);
        setQuantityKid(existingItem.childrenCount || 0);
      } else {
        const syncedQuantity = existingItem.quantity || 0;
        setQuantity(subType === "gift" ? Math.min(1, syncedQuantity) : syncedQuantity);
      }
    } else {
      if (subType !== "family") {
        setQuantity(0);
      } else {
        setQuantityAdult(0);
        setQuantityKid(0);
      }
    }
  }, [existingItem, subType]);

  React.useEffect(() => {
    if (isCompletelyBlocked && !(subType === "gift" && existingItem)) {
      const hadValues = subType === "family" 
        ? (quantityAdult > 0 || quantityKid > 0)
        : quantity > 0;
      
      if (hadValues) {
        if (subType === "family") {
          setQuantityAdult(0);
          setQuantityKid(0);
          onFamilyCountChange?.(String(id), 0, 0);
        } else {
          setQuantity(0);
          onQuantityChange?.(String(id), 0);
        }
      }
    }
  }, [isCompletelyBlocked, subType, id, quantityAdult, quantityKid, quantity, existingItem, onFamilyCountChange, onQuantityChange]);

  const maxQuantity = 10;
  const maxQuantityFamily = 8;

  const calculatedTotalPrice = React.useMemo(() => {
    if (subType === "family" && basePrice && additionalPersonPrice) {
      const totalPeople = quantityAdult + quantityKid;
      const minPeople = minPeopleRequired || 3;
      if (totalPeople >= minPeople) {
        const additionalPeople = Math.max(0, totalPeople - minPeople);
        return basePrice + additionalPeople * additionalPersonPrice;
      }
      return basePrice;
    } else if (subType === "gift") {
      return price;
    } else {
      return price * quantity;
    }
  }, [quantityAdult, quantityKid, quantity, subType, basePrice, additionalPersonPrice, minPeopleRequired, price]);


  function getDisabledStatus(typeButton: string, relativeBtn: string | null = null) {
    if (isCompletelyBlocked) return true;

    if (subType === "adult" || subType === "child" || subType === "gift" || subType === "daily") {
      return typeButton === "minus" ? quantity <= 0 : quantity >= maxQuantity;
    }
    if (subType === "family") {
      if (typeButton === "plus") {
        if (relativeBtn === "adult" && quantityAdult >= 2) return true;
        if (relativeBtn === "kid" && quantityAdult === 0) return true;
        return quantityAdult + quantityKid >= maxQuantityFamily;
      }
      if (typeButton === "minus") {
        if (relativeBtn === "adult") return quantityAdult <= 0;
        if (relativeBtn === "kid") return quantityKid <= 0;
      }
    }
    return false;
  }

  const includesItems = renderIncludesItems(includes);
  const disabledBtnPlusStatus = getDisabledStatus("plus");
  const disabledBtnMinusStatus = getDisabledStatus("minus");

  function renderIncludesItems(includes: string[]) {
    return includes.map((item, i) => {
      const serviceName = getServiceName(item);
      switch (item) {
        case "gym":
          return (
            <div className={styles.includes_item} key={i}>
              <img src={gym} alt={serviceName} />
              {serviceName}
            </div>
          );
        case "hotTub":
        case "pool":
          return (
            <div className={styles.includes_item} key={i}>
              <img src={pool} alt={serviceName} />
              {serviceName}
            </div>
          );
        case "spa":
        case "sauna":
          return (
            <div className={styles.includes_item} key={i}>
              <img src={sauna} alt={serviceName} />
              {serviceName}
            </div>
          );
        case "playroom":
          return (
            <div className={styles.includes_item} key={i}>
              <img src={playroom} alt={serviceName} />
              {serviceName}
            </div>
          );
        default:
          return null;
      }
    });
  }

  const renderCounters = (subType: string) => {
    if (subType === "family") {
      const totalPeople = quantityAdult + quantityKid;
      return (
        <div className={styles.quantityWrapper}>
          <div className={styles.quantity_family}>
            <p className={styles.quantity_text_family}>Взрослых:</p>
            <div className={styles.quantity_buttons}>
              <button
                className={styles.quantity_button_minus}
                onClick={() => handleQuantityDecrease(subType, "adult")}
                disabled={getDisabledStatus("minus", "adult")}
              />
              <span className={styles.quantity_value}>{quantityAdult}</span>
              <button
                className={styles.quantity_button_plus}
                onClick={() => handleQuantityIncrease(subType, "adult")}
                disabled={getDisabledStatus("plus", "adult")}
              />
            </div>
          </div>
          <div className={styles.quantity_family}>
            <p className={styles.quantity_text_family}>Детей:</p>
            <div className={styles.quantity_buttons}>
              <button
                className={styles.quantity_button_minus}
                onClick={() => handleQuantityDecrease(subType, "kid")}
                disabled={getDisabledStatus("minus", "kid")}
              />
              <span className={styles.quantity_value}>{quantityKid}</span>
              <button
                className={styles.quantity_button_plus}
                onClick={() => handleQuantityIncrease(subType, "kid")}
                disabled={getDisabledStatus("plus", "kid")}
              />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.quantity}>
          <p className={styles.quantity_text}>Количество:</p>
          <div className={styles.quantity_buttons}>
            <button
              className={styles.quantity_button_minus}
              onClick={() => handleQuantityDecrease(subType)}
              disabled={disabledBtnMinusStatus || isCompletelyBlocked}
            />
            <span className={styles.quantity_value}>{quantity}</span>
            <button
              className={styles.quantity_button_plus}
              onClick={() => handleQuantityIncrease(subType)}
              disabled={disabledBtnPlusStatus || isCompletelyBlocked}
            />
          </div>
        </div>
      );
    }
  };

  const handleQuantityDecrease = (typeSubs: string, relativeType: string | null = null) => {
    if (isCompletelyBlocked) return;

    if (typeSubs === "family") {
      if (relativeType === "kid") {
        const newCount = Math.max(0, quantityKid - 1);
        setQuantityKid(newCount);
        onFamilyCountChange?.(String(id), quantityAdult, newCount);
      } else {
        const newCount = Math.max(0, quantityAdult - 1);
        setQuantityAdult(newCount);
        if (newCount === 0) {
          setQuantityKid(0);
          onFamilyCountChange?.(String(id), 0, 0);
        } else {
          onFamilyCountChange?.(String(id), newCount, quantityKid);
        }
      }
    } else {
      const newQuantity = Math.max(0, quantity - 1);
      setQuantity(newQuantity);
      onQuantityChange?.(String(id), newQuantity);
    }
  };

  const handleQuantityIncrease = (typeSubs: string, relativeType: string | null = null) => {
    if (isCompletelyBlocked) return;

    if (typeSubs === "family") {
      if (relativeType === "kid") {
        if (quantityAdult === 0) return;
        const totalPeople = quantityAdult + quantityKid;
        if (totalPeople >= maxQuantityFamily) return;
        const newCount = Math.min(maxQuantityFamily - quantityAdult, quantityKid + 1);
        setQuantityKid(newCount);
        onFamilyCountChange?.(String(id), quantityAdult, newCount);
      } else {
        if (quantityAdult >= 2) return;
        const totalPeople = quantityAdult + quantityKid;
        if (totalPeople >= maxQuantityFamily) return;
        const newCount = Math.min(2, quantityAdult + 1);
        setQuantityAdult(newCount);
        onFamilyCountChange?.(String(id), newCount, quantityKid);
      }
    } else {
      const maxQty = subType === "gift" ? 1 : maxQuantity;
      const newQuantity = Math.min(maxQty, quantity + 1);
      setQuantity(newQuantity);
      onQuantityChange?.(String(id), newQuantity);
    }
  };

  const displayPrice = subType === "family" 
    ? calculatedTotalPrice 
    : quantity > 0
      ? calculatedTotalPrice 
      : price;

  const isSelected = quantity > 0 || (subType === "family" && quantityAdult + quantityKid >= 3);
  
  const canAddToCart = subType === 'family'
    ? (quantityAdult + quantityKid >= (minPeopleRequired || 3))
    : quantity > 0;

  return (
    <div id={String(id)} className={`${styles.item} ${isCompletelyBlocked ? styles.item_disabled : ""} ${subType === "family" ? styles.item_family : ""}`}>
      <div className={styles.header}>
        <div className={styles.labels}>
          {isNew && <span className={styles.label_new}>Новинка</span>}
          {isBest && <span className={styles.label_new}>Выгодно</span>}
        </div>
        
        <h3 className={styles.title}>{title}</h3>
        
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>

      <div className={`${styles.body} ${cardNotices.length > 0 ? styles.body_withNotice : ""}`}>
        <NoticeBanner notices={cardNotices} newStyles={styles.notice} />

        <div className={styles.infoWrapper}>
          <div className={styles.info}>
            <div className={styles.info_expiration}>
              <p>
                Срок действия:
              </p>
              <p>
                <span>{expiration_text}</span> <span>{durationNote || "(со дня оплаты)"}</span>
              </p>
            </div>
            <div className={styles.info_expiration}>
              <p>
                Время посещения: 
              </p>
              <p>
                <span>{visitingHours || "пн-вс, 7:00-24:00"}</span>
              </p>
            </div>
            {ageRestriction && (
              <div className={styles.info_expiration}>
                <p>
                  {subType === "family" ? "Дети" : "Возраст"}: 
                </p>
                <p>
                  <span>{ageRestriction}</span>
                </p>
              </div>
            )}
          </div>

          <div className={styles.includes}>
            <p className={styles.includes_title}>Включено:</p>
            <div className={styles.includes_items}>{includesItems}</div>
          </div>

          <div>{renderCounters(subType)}</div>
        </div>
        

        <div className={styles.priceWrapper}>
          <div className={styles.notes}>
            {!isGroupClassesIncluded && <div className={styles.note}>*Не включены групповые занятия</div>}
            {subType === "family" && minPeopleRequired && (
              <div className={styles.note}>*Минимальное количество людей - {minPeopleRequired}</div>
            )}
            {requiresAdult && <div className={styles.note}>*Не продается отдельно от взрослого!</div>}
            {note && <div className={styles.note}>{note}</div>}
          </div>

          <div className={styles.price}>
            {subType === "family" && quantityAdult + quantityKid < (minPeopleRequired || 3) ? (
              <span className={styles.pricePlaceholder}>
                0₽
              </span>
            ) : (
              <span className={styles.pricePlaceholder}>
                {displayPrice}₽
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
